import React, { useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import { ChevronDown } from "@piiqtechnologies/svg-icons";
import { PeekAvatar, PeekButton, PeekMenu, PeekMenuItem, PeekStack, PeekTypography, useTheme, } from "@piiqtechnologies/ui-components";
import { buildOrgPath, PATHS } from "@constants/Paths";
import { useOptionalActiveOrganization } from "@stores/ActiveOrganizationContext";
import { getNameInitials } from "@utils/nameInitials";
import messages from "../messages.i18n";
const AVATAR_SX = {
    width: 20,
    height: 20,
    borderRadius: 0.6,
    fontSize: 8,
    lineHeight: "12px",
    fontWeight: "medium",
    bgcolor: "primary.main",
    color: "primary.contrastText",
};
// The menu is wider than the trigger it hangs off (204px against the sidebar's 151px), so an
// organization name has room to breathe in the list even when it is truncated in the trigger.
const MENU_SX = {
    "& .MuiPaper-root": {
        width: 204,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "grey.100",
        boxShadow: "0px 2px 23px rgba(227, 227, 227, 0.55)",
        "& .MuiMenu-list": { padding: "0 5px 5px" },
    },
};
// PeekMenuItem centers its content and draws a rule between adjacent items. Neither suits this
// menu: the rows are left-aligned around an avatar and read as a single grouped block.
const MENU_ITEM_SX = {
    "&.MuiMenuItem-root": {
        justifyContent: "flex-start",
        minHeight: 36,
        gap: 0.5,
        padding: "8px 4px",
        borderRadius: 2,
        "~ .MuiMenuItem-root": { borderTop: "none" },
    },
};
/**
 * Persistent sidebar control that names the active organization and, for multi-org users,
 * lets them switch to any other organization they belong to. Self-contained: it reads the
 * active organization off context and navigates itself, taking no props.
 *
 * Unlike the full-screen picker (deliberately no pre-selection, Continue disabled until a
 * row is chosen), this is a quick-switch: a single click on a menu entry switches
 * immediately, with no confirmation step. Switching always lands on the new organization's
 * home — never the current route re-scoped to a different org, which could point at a
 * resource the new organization doesn't own.
 */
export const OrganizationSwitcher = () => {
    const intl = useIntl();
    const theme = useTheme();
    const navigate = useNavigate();
    const context = useOptionalActiveOrganization();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleOpen = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    // Outside the resolver tree (e.g. staff surfaces, which never mount under /o/:orgId) there
    // is no active organization to name, so the switcher renders nothing.
    if (!context)
        return null;
    const { activeOrg, memberships } = context;
    const identity = (React.createElement(React.Fragment, null,
        React.createElement(PeekAvatar, { variant: "rounded", sx: AVATAR_SX }, getNameInitials(activeOrg.name)),
        React.createElement(PeekTypography, { noWrap: true, color: "text.primary", variant: "body1", sx: { flex: 1, textAlign: "left" } }, activeOrg.name)));
    // Single membership: the organization is still named, but with no box, chevron or click
    // affordance, since there is nothing to switch to.
    if (memberships.length <= 1) {
        return (React.createElement(PeekStack, { direction: "row", alignItems: "center", gap: 0.4, sx: { minWidth: 0, px: 0.5 } }, identity));
    }
    const handleSelect = (orgId) => () => {
        handleClose();
        navigate(buildOrgPath(orgId, PATHS.HOME));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(PeekButton, { variant: "text", onClick: handleOpen, "aria-label": intl.formatMessage(messages.aiDiscoverabilityDashboardSwitchOrganization), sx: {
                justifyContent: "flex-start",
                width: "100%",
                minWidth: 0,
                height: 40,
                padding: "10px 5px",
                textTransform: "none",
                borderRadius: 2,
                border: "0.5px solid",
                borderColor: "grey.100",
                backgroundColor: "background.paper",
                boxShadow: "0px 2px 11px 1px rgba(210, 210, 210, 0.13)",
            } },
            React.createElement(PeekStack, { direction: "row", alignItems: "center", gap: 0.4, sx: { minWidth: 0, width: "100%" } },
                identity,
                React.createElement(ChevronDown, { fontSize: 16 }))),
        React.createElement(PeekMenu, { anchorEl: anchorEl, open: open, onClose: handleClose, anchorOrigin: { vertical: "bottom", horizontal: "left" }, transformOrigin: { vertical: "top", horizontal: "left" }, sx: MENU_SX },
            React.createElement(PeekStack, { sx: { padding: "8px 12px 5px" } },
                React.createElement(PeekTypography, { variant: "body1", fontWeight: "medium", color: "grey.400", textTransform: "uppercase" }, intl.formatMessage(messages.aiDiscoverabilityDashboardSwitchOrganization))),
            memberships.map((membership) => {
                const isActive = membership._id === activeOrg._id;
                return (React.createElement(PeekMenuItem, { key: membership._id, selected: isActive, onClick: handleSelect(membership._id), selectedBgColor: alpha(theme.palette.secondary.main, 0.1), sx: MENU_ITEM_SX },
                    React.createElement(PeekAvatar, { variant: "rounded", sx: AVATAR_SX }, getNameInitials(membership.name)),
                    React.createElement(PeekTypography, { noWrap: true, variant: "body1", fontSize: 13, fontWeight: "medium", color: isActive ? "secondary.main" : "text.primary" }, membership.name)));
            }))));
};
export default OrganizationSwitcher;
